// @ts-check
import { gql, useMutation, useQuery } from '@apollo/client';
import React from 'react';
import { Box } from '~/design-system';
import Notification from '../Notification';
const EMPTY = '(empty)';

/**
 * Step where the collection is selected
 * @param {ReturnType<useChooseCollection>} props
 */
export default function ChooseProductList({
  loading,
  savedCollectionId,
  setSelectedCollection,
  collections,
  notification,
  cancelNotification,
}) {
  if (loading) return null;

  return (
    <React.Fragment>
      <Box>
        <select
          //@ts-ignore
          sx={{ height: 24 }}
          onChange={({ target: { value } }) => {
            if (value === EMPTY) {
              setSelectedCollection(undefined);
            } else {
              setSelectedCollection(value);
            }
          }}
          defaultValue={savedCollectionId}
        >
          <option value={EMPTY}>Select</option>
          {collections?.map(({ collectionId, name }) => (
            <option key={collectionId} value={collectionId}>
              {name}
            </option>
          ))}
        </select>
      </Box>

      {notification && (
        <Notification
          onClose={cancelNotification}
          message={notification?.message}
          variant={notification?.success ? 'success' : 'error'}
        />
      )}
    </React.Fragment>
  );
}

/**
 * @param {object} props
 * @param {string} props.workspaceId
 * @param {string} props.importId
 * @return
 */
export function useChooseCollection({ workspaceId, importId }) {
  const requestResult = useQuery(query, {
    variables: { workspaceId, importId },
  });
  const { savedCollectionId } = afterQuery(requestResult);

  const [isSubmiting, setIsSubmiting] = React.useState(
    /** @type {boolean} */ (false)
  );
  const [_updateImportCollectionId] = useMutation(UPDATE_COLLECTION_ID);
  const [selectedCollection, setSelectedCollection] = React.useState(
    /** @type {string | undefined} */ (undefined)
  );
  const [isDirty, setIsDirty] = React.useState(/** @type {boolean} */ (false));
  /** @type {[import('../Notification').Notification | undefined, React.Dispatch<React.SetStateAction<import('../Notification').Notification | undefined>>]} */
  const [notification, setNotification] = React.useState();
  const [errorCode, setErrorCode] = React.useState(
    /** @type {'missing_externalId' | 'request_id_invalid' | undefined}*/ (
      undefined
    )
  );

  if (!requestResult.loading && !isDirty) {
    setSelectedCollection(savedCollectionId);
    setIsDirty(true);
  }

  async function updateImportCollectionId() {
    if (!selectedCollection) return null;
    try {
      setIsSubmiting(true);
      const { data } = await _updateImportCollectionId({
        variables: { importId, collectionId: selectedCollection },
      });
      if (!data?.result?.success) {
        setNotification({
          message: 'There was an error, please try again!',
        });
      }
      setIsSubmiting(false);
      setIsDirty(false);
    } catch (err) {
      if (err.message === 'collection_missing_externalId') {
        setErrorCode('missing_externalId');
      } else if (err.message === 'requestIds_not_in_collection') {
        setErrorCode('request_id_invalid');
      } else {
        setErrorCode(undefined);
        setNotification({
          message: 'There was an error, please try again!',
        });
      }
      setIsSubmiting(false);
      setIsDirty(false);
      return { error: err };
    }
  }

  return {
    errorCode,
    importId,
    updateImportCollectionId,
    setSelectedCollection,
    notification,
    cancelNotification: () => {
      setNotification(undefined);
    },
    ...requestResult,
    ...afterQuery(requestResult),
    isValid: !isSubmiting && selectedCollection && selectedCollection !== EMPTY,
  };
}

function afterQuery({ data }) {
  const { collectionId: savedCollectionId } = data?.workspace?.dataImport || {};

  const collections = data?.workspace?.collections?.nodes
    ?.filter((collection) => {
      return collection.collectionType === 'PRODUCTLIST';
    })
    .sort((a, b) => {
      const nameA = a.name.toString();
      const nameB = b.name.toString();
      return nameA > nameB ? 1 : -1;
    });
  return {
    collections,
    savedCollectionId,
  };
}

/** @type {typeof import('./ChooseProductList.generated').ChooseProductListPageDocument} */
const query = gql`
  query ChooseProductListPage($workspaceId: ID!, $importId: ID!) {
    workspace(workspaceId: $workspaceId) {
      workspaceId
      dataImport(importId: $importId) {
        importId
        collectionId
      }
      collections {
        nodes {
          collectionType
          collectionId
          name
          ... on Event {
            eventId
          }
          ... on ShareList {
            shareListId
          }
          ... on ProductList {
            productListId
          }
        }
      }
    }
  }
`;
/** @type {typeof import('./ChooseProductList.generated').UpdateCollectionIdDocument} */
export const UPDATE_COLLECTION_ID = gql`
  mutation updateCollectionId($importId: ID!, $collectionId: ID!) {
    result: DataImportUpdate(
      input: { importId: $importId, patch: { collectionId: $collectionId } }
    ) {
      code
      success
      message
      dataImport {
        importId
        collectionId
      }
    }
  }
`;
